import Link from "@components/Link";
import SocialIcons from "@components/SocialIcons";
import { ThemeEnum } from "@components/ThemeProvider/ThemeProvider";
import useGTMDispatch from "@src/app/hooks/useGTMDispatch";
import clsx from "clsx";
import type { MouseEvent } from "react";
import { useLocation } from "react-router";

const mainNavLinks = [
  { label: "Events", href: "/events" },
  { label: "Newsletter", href: "/newsletter" },
  { label: "About Us", href: "/about-us" },
  { label: "Contributors", href: "/contributors" },
];

type MainNavProps = {
  theme?: ThemeEnum;
};

export default function MainNav({ theme = ThemeEnum.light }: MainNavProps) {
  const location = useLocation();
  const sendDataToGTM = useGTMDispatch();
  const isDefault = theme === ThemeEnum.light;
  const pageUrl =
    typeof window !== "undefined"
      ? window.location.href
      : `https://www.thinkchina.sg${location.pathname}${location.search}`;

  const trackMainNavClick = (label: string) => {
    if (label.toLowerCase() === "newsletter") {
      sendDataToGTM({
        event: "tcCustomEvent",
        eventCategory: "newsletter",
        eventAction: "click",
        eventLabel: pageUrl,
      });
      return;
    }

    sendDataToGTM({
      event: "tcCustomEvent",
      eventCategory: "navigation",
      eventAction: "click",
      eventLabel: label.toLowerCase(),
    });
  };

  const trackHeaderSocialClick = (event: MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement | null;
    const anchor = target?.closest("a");
    const href = anchor?.getAttribute("href") || "";
    if (!href.includes("facebook.com")) return;

    sendDataToGTM({
      event: "tcCustomEvent",
      eventCategory: "social link",
      eventAction: "click",
      eventLabel: "facebook",
    });
  };

  return (
    <div
      data-testid="main-nav"
      className="hidden flex-1 flex-row items-center justify-end lg:flex"
    >
      <div className="flex items-center gap-3 py-4 pl-3">
        <div className="flex items-center gap-0">
          {mainNavLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              onClick={() => {
                trackMainNavClick(link.label);
              }}
              className="hover:bg-button-ghost-hover flex h-8 shrink-0 items-center justify-center rounded-[100px] px-3 py-2"
            >
              <p
                className={clsx(
                  "text-button-1 shrink-0",
                  isDefault
                    ? "text-button-tertiary-label"
                    : "text-button-ghost-label-on-dark",
                )}
              >
                {link.label}
              </p>
            </Link>
          ))}
        </div>
        <div onClickCapture={trackHeaderSocialClick}>
          <SocialIcons />
        </div>
      </div>
    </div>
  );
}
